import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import './AdminHeader.scss';
import './CommonHeaderElements.scss';
import CommonHeaderElements from './CommonHeaderElements';
import '@aurodesignsystem/auro-hyperlink';
import '@aurodesignsystem/auro-button';

const AdminHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768
  });

  const mainNavItems = [
    { path: '/flight-ranges', label: 'Flight ranges' },
    { path: '/partners', label: 'Partners' }
  ];

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setViewport({
        width,
        isMobile: width < 768
      });

      // Close mobile menu when resizing to desktop
      if (width >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActivePath = (path: string) => location.pathname.startsWith(path);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="admin-header" role="banner">
      <div className="admin-header__container">
        <div className="admin-header__left">
          <CommonHeaderElements
            onSignOut={() => viewport.isMobile && setMobileMenuOpen(false)}
            hideSignOut={true}
          />
        </div>

        {/* Mobile menu toggle button */}
        {viewport.isMobile && (
          <auro-button
            ref={(el: any) => {
              if (el) {
                el.addEventListener('click', toggleMobileMenu);
                el.setAttribute('aria-expanded', mobileMenuOpen.toString());
                el.setAttribute('aria-controls', 'main-navigation');
                el.setAttribute('aria-label', 'Toggle navigation menu');
              }
            }}
            className={`admin-header__mobile-toggle${mobileMenuOpen ? ' open' : ''}`}
            variant="tertiary"
            style={{
              background: "transparent",
              border: "none",
              padding: "8px",
              minWidth: "auto",
              width: "40px",
              height: "40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <span className="admin-header__mobile-toggle-bar"></span>
            <span className="admin-header__mobile-toggle-bar"></span>
            <span className="admin-header__mobile-toggle-bar"></span>
          </auro-button>
        )}

        <nav
          id="main-navigation"
          className={`admin-header__nav${mobileMenuOpen ? ' admin-header__nav--open' : ''}${viewport.isMobile ? ' admin-header__nav--mobile' : ''}`}
          role="navigation"
          aria-label="Main navigation"
        >
          {mainNavItems.map(({ path, label }) => (
            <auro-hyperlink
              key={path}
              ref={(el: any) => {
                if (el) {
                  el.addEventListener('click', (e: Event) => {
                    e.preventDefault();
                    navigate(path);
                    viewport.isMobile && setMobileMenuOpen(false);
                  });
                  el.setAttribute('href', path);
                }
              }}
              className={`admin-header__nav-link${isActivePath(path) ? ' admin-header__nav-link--active' : ''}`}
              aria-current={isActivePath(path) ? 'page' : undefined}
              font-size="16px"
              nav
              style={{
                "--ds-auro-hyperlink-default-color-default": isActivePath(path)
                  ? "#01426A"
                  : "#0074CA",
                "--ds-auro-hyperlink-default-color-hover": "#01426A",
                color: isActivePath(path) ? "#01426A" : "#0074CA",
                textDecoration: isActivePath(path) ? 'underline' : 'none',
                textUnderlineOffset: '8px',
                textDecorationThickness: '2px',
                fontWeight: '400',
                transition: 'color 0.2s ease'
              } as React.CSSProperties}
            >
              {label}
            </auro-hyperlink>
          ))}

          <auro-button
            ref={(el: any) => {
              if (el) {
                el.addEventListener('click', () => {
                  auth.removeUser();
                  viewport.isMobile && setMobileMenuOpen(false);
                });
              }
            }}
            variant="secondary"
            size="sm"
            aria-label="Sign out of application"
            className="admin-header__sign-out"
            responsive
            style={{
              borderRadius: "6px",
              background: "transparent",
              color: "var(--color-primary, #0074CA)",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "400",
              marginLeft: "auto"
            }}
          >
            Sign out
          </auro-button>
        </nav>
      </div>
    </header>
  );
};

export default AdminHeader;